'use client';

import { Navigate } from 'react-router-dom';
import { apiFetch } from '@/lib/utils/api';
import { CerFormView } from '@slims/ui/organisms';
import { useParams, useRouter } from '@/lib/next/navigation';
import { useState } from 'react';
import { toast } from 'sonner';
import { type AdminPick, AdminUserPicker } from '@/components/admin/admin-user-picker';
import { useScopeTree } from '@/hooks/use-scope-tree';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Creazione di una CER. La modifica di una CER esistente sta tutta nel suo
 * dettaglio: `/cer/orgs/:id` ci reindirizza (INB-374).
 */
export default function CerFormPage() {
	const { id } = useParams<{ id?: string }>();
	const role = useAuthStore((s) => s.role);
	const loaded = useAuthStore((s) => s.loaded);
	if (id) return <Navigate to={`/cer/orgs/${id}/details`} replace />;
	if (!loaded) return null;
	if (role !== 'superadmin') return <Navigate to="/dashboard" replace />;
	return <CerCreateWiring />;
}

function CerCreateWiring() {
	const router = useRouter();
	const close = () => router.push('/cer');
	const fetchAuth = useAuthStore((s) => s.fetchAuth);
	const { refetch: refetchScopeTree } = useScopeTree();

	const [nome, setNome] = useState('');
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	// Primo amministratore facoltativo (prima l'email; vedi AdminUserPicker).
	const [adminPick, setAdminPick] = useState<AdminPick>(emptyAdminPick);

	async function handleCreate() {
		setError('');
		if (!nome.trim()) {
			setError('La denominazione è obbligatoria');
			return;
		}
		// The admin is optional: only validated/sent when a valid email was entered.
		if (adminPick.emailValid && !adminPick.existing && adminPick.password.length < 8) {
			setError('La password admin deve avere almeno 8 caratteri');
			return;
		}
		const adminBody = !adminPick.emailValid
			? {}
			: adminPick.existing
				? { adminUserId: adminPick.existing.id }
				: {
						adminEmail: adminPick.email,
						adminPassword: adminPick.password,
						adminName: adminPick.fullName,
					};
		setSaving(true);
		const res = await apiFetch('/api/admin/orgs', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ nome: nome.trim(), ...adminBody }),
		});
		if (!res.ok) {
			const body = (await res.json().catch(() => ({}))) as { error?: string };
			setError(body.error ?? 'Errore nella creazione');
			setSaving(false);
			return;
		}
		// La nuova CER deve comparire subito in ogni selettore (lista CER,
		// switcher Soci/Dati) senza attendere il realtime o un reload.
		await Promise.all([fetchAuth(), refetchScopeTree()]);
		// Come per la modifica: senza, la creazione si chiudeva in silenzio (INB-345).
		toast.success(`CER «${nome.trim()}» creata`);
		close();
	}

	return (
		<CerFormView
			nome={nome}
			saving={saving}
			error={error}
			saveDisabled={adminPick.checking}
			canManageAdmins
			initialAdmin={
				<AdminUserPicker
					lookupUrl="/api/admin/user-lookup"
					roleLabel="amministratore"
					onChange={setAdminPick}
					size="md"
				/>
			}
			onNameChange={setNome}
			onSave={() => void handleCreate()}
			onClose={close}
		/>
	);
}

const emptyAdminPick: AdminPick = {
	email: '',
	fullName: '',
	password: '',
	existing: null,
	emailValid: false,
	checking: false,
};
